// Wipe every browser-side copy of a user's data at each identity change (sign-out, inactivity / revoked / expired
// session, and sign-in).
//
// WHY (Prod audit D11, 2026-10-21): the service worker serves health reads (/api/twin, /api/medications,
// /api/dashboard, …) CACHE-FIRST, and those caches survived sign-out — localStorage / sessionStorage were cleared,
// CacheStorage was not. After an inactivity sign-out, the next person to sign in on the same browser saw the
// PREVIOUS user's medications until the background revalidation replaced them. sw.js now also partitions its cache
// per user, so a missed clear still cannot cross users; this function is the first line of defence.
//
// Never throws, never blocks longer than CLEAR_TIMEOUT_MS: a stuck IndexedDB delete (blocked by an open connection)
// must not strand a sign-in or a sign-out.
const RUNTIME_CACHE_PREFIX = 'runtime-';
const USER_DATABASES = ['healist-offline', 'healist-offline-alerts'];
const CLEAR_TIMEOUT_MS = 1500;

function deleteDatabase(name) {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    } catch (_) {
      resolve();
    }
  });
}

export async function clearUserData() {
  const tasks = [];
  if (typeof caches !== 'undefined') {
    tasks.push(caches.keys()
      .then((keys) => Promise.all(keys.filter((k) => k.startsWith(RUNTIME_CACHE_PREFIX)).map((k) => caches.delete(k))))
      .catch(() => {}));
  }
  if (typeof indexedDB !== 'undefined') {
    USER_DATABASES.forEach((name) => tasks.push(deleteDatabase(name)));
  }
  try {
    // The SW also drops its own handles / in-memory state for the alerts DB.
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_USER_DATA' });
  } catch (_) { /* no controller yet (first visit) — nothing cached by the SW either */ }
  await Promise.race([Promise.allSettled(tasks), new Promise((r) => setTimeout(r, CLEAR_TIMEOUT_MS))]);
}

export const _internals = { RUNTIME_CACHE_PREFIX, USER_DATABASES, CLEAR_TIMEOUT_MS };
